import { gql, TypedDocumentNode, useMutation, useQuery } from "@apollo/client";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { GridColDef } from "@mui/x-data-grid";
import XLSX from "xlsx";
import { EarlyEstimateRow, Mutation, MutationSaveEarlyEstimatesArgs, Query, QueryEarlyEstimatesArgs } from "_graphql-types";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNotify, useStore } from "react-admin";
import { formatMonth, isDraftChanged, monthArg, previousMonth } from "../clientInvestments/logic";
import {
  confirmDiscard,
  EntryInput,
  FilterBar,
  MonthField,
  Page,
  QueryState,
  ReadOnlyCell,
  ReportGrid,
  SaveActions,
  Section,
  textCol,
} from "../clientInvestments/shared";
import {
  ESTIMATE_FIELDS,
  EstimateDraft,
  EstimateField,
  estimateInitial,
  estimateInputs,
  MAX_ABS_GROSS_RETURN_PCT,
  parseReturnsSheet,
  templateRows,
  toDisplay,
  UPLOAD_COLUMNS,
} from "./logic";

const EARLY_ESTIMATES: TypedDocumentNode<Pick<Query, "earlyEstimates">, QueryEarlyEstimatesArgs> = gql`
  query EarlyEstimates($month: Date!) {
    earlyEstimates(month: $month) {
      rcgFundId
      name
      isEstimate
      systemGrossEndBal
      systemNetEndBal
      systemGrossReturn
      systemSpread
      netReturn
      grossEndBal
      netEndBal
      grossReturn
      spread
      version
    }
  }
`;

const SAVE_EARLY_ESTIMATES: TypedDocumentNode<Pick<Mutation, "saveEarlyEstimates">, MutationSaveEarlyEstimatesArgs> = gql`
  mutation SaveEarlyEstimates($month: Date!, $estimates: [EarlyEstimateInput!]!) {
    saveEarlyEstimates(month: $month, estimates: $estimates)
  }
`;

interface Row extends EarlyEstimateRow {
  id: number;
  draft: EstimateDraft;
  initial: EstimateDraft;
}

const SYSTEM: Record<EstimateField, "systemGrossEndBal" | "systemNetEndBal" | "systemGrossReturn" | "systemSpread"> = {
  grossEndBal: "systemGrossEndBal",
  netEndBal: "systemNetEndBal",
  grossReturn: "systemGrossReturn",
  spread: "systemSpread",
};

/** How often the grid picks up other people's saves while this user has nothing pending. */
const ESTIMATES_POLL_MS = 30_000;

export function EarlyEstimates() {
  const notify = useNotify();
  const [month, setMonth] = useStore<string>("earlyEstimates.month", previousMonth());
  const [drafts, setDrafts] = useState<ReadonlyMap<number, EstimateDraft>>(new Map());
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(EARLY_ESTIMATES, {
    variables: { month: monthArg(month) },
  });
  const [save, saving] = useMutation(SAVE_EARLY_ESTIMATES);
  useEffect(() => setDrafts(new Map()), [month]);

  const estimates = data?.earlyEstimates ?? [];
  const rows = useMemo(
    () =>
      estimates.map((e): Row => {
        const initial = estimateInitial(e);
        return { ...e, id: e.rcgFundId, initial, draft: drafts.get(e.rcgFundId) ?? initial };
      }),
    [estimates, drafts]
  );

  const pending = rows.filter(r => isDraftChanged(r.draft, r.initial)).length;
  // Several people work the month at once: pick up their saves while this user has nothing
  // pending (polling while editing would only move the ground under them).
  useEffect(() => {
    if (pending === 0) startPolling(ESTIMATES_POLL_MS);
    else stopPolling();
    return stopPolling;
  }, [pending, startPolling, stopPolling]);

  const setField = useCallback(
    (row: Row, field: EstimateField, value: string) =>
      setDrafts(prev => new Map(prev).set(row.id, { ...(prev.get(row.id) ?? row.initial), [field]: value })),
    []
  );

  const columns = useMemo((): GridColDef<Row>[] => {
    const entryCol = (field: EstimateField, headerName: string): GridColDef<Row> => ({
      field: `entry.${field}`,
      headerName,
      width: 150,
      align: "right",
      headerAlign: "right",
      valueGetter: (_v, row) => row.draft[field],
      renderCell: ({ row }) => {
        const system = toDisplay(field, row[SYSTEM[field]]);
        if (!row.isEstimate) return <ReadOnlyCell text={row.draft[field]} reason="Reconciled: this fund's month is read-only" />;
        return (
          <EntryInput
            value={row.draft[field]}
            initial={row.initial[field]}
            placeholder={system}
            title={system ? `System: ${system}` : "No system value"}
            onChange={v => setField(row, field, v)}
            label={`${row.name} ${headerName}`}
          />
        );
      },
    });
    return [
      textCol<Row>("name", "RCG Fund", 300),
      entryCol("grossEndBal", "Gross End Bal $M"),
      entryCol("netEndBal", "Net End Bal $M"),
      entryCol("grossReturn", "Gross Return %"),
      entryCol("spread", "Spread %"),
      {
        field: "netReturn",
        headerName: "Net Return %",
        type: "number",
        width: 130,
        valueFormatter: (v: number | null | undefined) => toDisplay("grossReturn", v),
      },
    ];
  }, [setField]);

  const groups = useMemo(
    () => [
      {
        groupId: "Overrides",
        headerName: "Overrides (blank: system value)",
        headerAlign: "center" as const,
        children: ESTIMATE_FIELDS.map(f => ({ field: `entry.${f}` })),
      },
    ],
    []
  );

  const [uploadErrors, setUploadErrors] = useState<{ file: string; errors: string[] } | null>(null);

  const onDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(templateRows(estimates)), "Gross returns");
    XLSX.writeFile(wb, `early-estimates-${month}.xlsx`);
  };

  const onUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // the same file can be picked again after fixing it
    if (!file) return;
    setUploadErrors(null);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      // Cells as displayed, so a cell formatted as a percentage reads "1.25%", not 0.0125.
      const table: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });
      const { returns, errors } = parseReturnsSheet(table, estimates);
      if (errors.length) {
        setUploadErrors({ file: file.name, errors });
        return;
      }
      if (returns.size === 0) {
        notify(`${file.name} has no gross returns to fill in`, { type: "info" });
        return;
      }
      setDrafts(prev => {
        const next = new Map(prev);
        for (const [id, grossReturn] of returns) {
          const row = rows.find(r => r.id === id);
          if (row) next.set(id, { ...(prev.get(id) ?? row.initial), grossReturn });
        }
        return next;
      });
      notify(`Filled ${returns.size} gross return${returns.size === 1 ? "" : "s"} from ${file.name}: review them, then Save`, { type: "success" });
    } catch (err) {
      setUploadErrors({ file: file.name, errors: [`Couldn't read the file: ${err instanceof Error ? err.message : String(err)}`] });
    }
  };

  const onSave = async () => {
    try {
      const input = estimateInputs(estimates, drafts);
      if (input.length === 0) {
        notify("Nothing has changed", { type: "info" });
        return;
      }
      await save({ variables: { month: monthArg(month), estimates: input } });
      await refetch();
      setDrafts(new Map());
      notify(`Saved ${input.length} fund${input.length === 1 ? "" : "s"}`, { type: "success" });
    } catch (e) {
      // A conflict (someone else saved one of these funds) or any other refusal: show the latest
      // values and keep this user's edits, so they can review and save again.
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
      // A failed reload shows in the query's own error state; it must not hide the save's error, and
      // its rejection is handled there, not here.
      refetch().catch(() => undefined);
    }
  };

  return (
    <Page
      title="Early Estimates"
      description="Overrides of the NAV model's estimate; the system value shows (greyed) when blank and on hover. Reconciled funds are read-only. A blank spread saves the system spread."
    >
      <FilterBar>
        <MonthField value={month} onChange={m => confirmDiscard(pending) && setMonth(m)} />
        <Button variant="outlined" startIcon={<FileDownloadIcon />} disabled={estimates.length === 0} onClick={onDownloadTemplate}>
          Download template
        </Button>
        <Button variant="outlined" component="label" startIcon={<FileUploadIcon />} disabled={estimates.length === 0}>
          Upload gross returns
          {/* display none, not hidden: a global input style overrides the attribute. */}
          <input type="file" style={{ display: "none" }} accept=".xlsx,.xls,.csv" onChange={onUpload} />
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ flexBasis: "100%" }}>
          An .xlsx or .csv with the columns <b>{UPLOAD_COLUMNS.fundId}</b> and <b>{UPLOAD_COLUMNS.grossReturn}</b>, in percent
          (1.25 = 1.25%). Blank leaves a fund as it is; other columns are ignored. The values fill the grid for review; nothing is
          saved until you Save.
        </Typography>
      </FilterBar>
      {uploadErrors && (
        <Alert severity="error" onClose={() => setUploadErrors(null)}>
          {uploadErrors.file} was not applied; fix these and upload it again (monthly gross returns must be within ±
          {MAX_ABS_GROSS_RETURN_PCT}%):
          <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
            {uploadErrors.errors.slice(0, 20).map(m => (
              <li key={m}>{m}</li>
            ))}
            {uploadErrors.errors.length > 20 && <li>...and {uploadErrors.errors.length - 20} more</li>}
          </ul>
        </Alert>
      )}
      <QueryState loading={loading} error={error} />
      <Section
        title={`Funds, ${formatMonth(monthArg(month))}`}
        actions={<SaveActions pending={pending} saving={saving.loading} onSave={onSave} onDiscard={() => setDrafts(new Map())} />}
      >
        <ReportGrid<Row> rows={rows} columns={columns} columnGroupingModel={groups} noRowsLabel="No funds for this month" />
      </Section>
    </Page>
  );
}
